import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { KalemCombobox } from "./KalemCombobox";
import { formatTL } from "../lib/format";
import { ODEME_SEKILLERI, bugun } from "../lib/sabitler";
import { Loader2 } from "lucide-react";

const EMPTY = {
  tarih: "",
  butce_kalemi_id: "",
  tedarikci: "",
  matrah: "",
  kdv: "",
  belge_no: "",
  odeme_sekli: "",
  aciklama: "",
};

export const GiderForm = ({ kalemler, initial, onSubmit, submitting, submitLabel = "Kaydet" }) => {
  // KURAL: Personel modülünden beslenen maaş kalemleri elle gider girişine kapalıdır
  const secilebilirKalemler = useMemo(
    () => (kalemler || []).filter((k) => k.kaynak !== "personel" && k.kalem_adi !== "Brüt Ücret ve Maaş Ödemeleri"),
    [kalemler]
  );
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    tarih: bugun(),
    ...(initial
      ? {
          ...initial,
          matrah: initial.matrah != null ? String(initial.matrah) : "",
          kdv: initial.kdv != null ? String(initial.kdv) : "",
          tedarikci: initial.tedarikci || "",
          belge_no: initial.belge_no || "",
          aciklama: initial.aciklama || "",
          odeme_sekli: initial.odeme_sekli || "",
          butce_kalemi_id: initial.butce_kalemi_id || "",
        }
      : {}),
  }));

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toplam = useMemo(
    () => (parseFloat(form.matrah) || 0) + (parseFloat(form.kdv) || 0),
    [form.matrah, form.kdv]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.tarih) return toast.error("Tarih zorunludur.");
    if (!form.butce_kalemi_id) return toast.error("Gider kalemi seçin.");
    const matrah = parseFloat(form.matrah);
    if (!matrah || matrah <= 0) return toast.error("Matrah 0'dan büyük olmalıdır.");
    if (!form.odeme_sekli) return toast.error("Ödeme şekli seçin.");

    onSubmit({
      tarih: form.tarih,
      butce_kalemi_id: form.butce_kalemi_id,
      tedarikci: form.tedarikci.trim(),
      matrah,
      kdv: parseFloat(form.kdv) || 0,
      belge_no: form.belge_no.trim(),
      odeme_sekli: form.odeme_sekli,
      aciklama: form.aciklama.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="gider-form">
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div className="space-y-1.5">
          <Label>Tarih *</Label>
          <Input
            type="date"
            value={form.tarih}
            onChange={(e) => set("tarih", e.target.value)}
            className="h-12"
            data-testid="gider-form-tarih-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Gider Kalemi *</Label>
          <KalemCombobox
            kalemler={secilebilirKalemler}
            value={form.butce_kalemi_id}
            onChange={(v) => set("butce_kalemi_id", v)}
            testid="gider-form-kalem-combobox"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Tedarikçi</Label>
          <Input
            placeholder="Tedarikçi / ödenen"
            value={form.tedarikci}
            onChange={(e) => set("tedarikci", e.target.value)}
            className="h-12"
            data-testid="gider-form-tedarikci-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Belge No</Label>
          <Input
            placeholder="FT2026..."
            value={form.belge_no}
            onChange={(e) => set("belge_no", e.target.value)}
            className="h-12"
            data-testid="gider-form-belge-no-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Matrah (KDV hariç) * ₺</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0,00"
            value={form.matrah}
            onChange={(e) => set("matrah", e.target.value)}
            className="h-12 text-right tabular-nums"
            data-testid="gider-form-matrah-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>KDV ₺</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0,00"
            value={form.kdv}
            onChange={(e) => set("kdv", e.target.value)}
            className="h-12 text-right tabular-nums"
            data-testid="gider-form-kdv-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Ödeme Şekli *</Label>
          <Select value={form.odeme_sekli} onValueChange={(v) => set("odeme_sekli", v)}>
            <SelectTrigger className="h-12" data-testid="gider-form-odeme-select">
              <SelectValue placeholder="Seçin" />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {ODEME_SEKILLERI.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Açıklama</Label>
          <Input
            placeholder="Açıklama"
            value={form.aciklama}
            onChange={(e) => set("aciklama", e.target.value)}
            className="h-12"
            data-testid="gider-form-aciklama-input"
          />
        </div>
      </div>

      <div
        className="flex items-center justify-between rounded-lg bg-[hsl(var(--primary))] px-4 py-3 text-white"
        data-testid="gider-form-toplam-display"
      >
        <span className="text-sm text-white/70">Toplam (Matrah + KDV)</span>
        <span className="font-display text-xl font-bold tabular-nums text-[hsl(var(--accent))]">
          {formatTL(toplam, 2)}
        </span>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="h-12 w-full sm:w-auto sm:min-w-[200px] rounded-lg text-base font-medium transition-colors duration-150 active:scale-[0.98]"
        data-testid="gider-form-submit-button"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Kaydediliyor…
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
};
